import { Button } from '@monorepo/shared/componentsV2/Button';
import {
  InfoCard,
  InfoCardVariant,
} from '@monorepo/shared/componentsV2/InfoCard';
import { DatasetResponse, JoinStepConditionRequest } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  getUserFriendlyResourceTypeDatasetName,
  tryToGetPairedPropertiesForResourceTypeJoin,
} from './helpers';

const StyledInfoCard = styled(InfoCard)`
  max-width: 32rem;
  margin-top: 1rem;
`;

const StyledButton = styled(Button)`
  display: flex;
  margin-left: -0.5rem;
  font-size: 0.75rem;
`;

interface JoinOnResourceTypeCallToActionProps {
  stepInputDataset: DatasetResponse;
  joinToDataset: DatasetResponse;
  conditions: {
    update: (index: number, value: Partial<JoinStepConditionRequest>) => void;
    push: (value: Partial<JoinStepConditionRequest>) => void;
    value: Partial<JoinStepConditionRequest>[];
  };
}

export function JoinOnResourceTypeCallToAction({
  stepInputDataset,
  joinToDataset,
  conditions,
}: JoinOnResourceTypeCallToActionProps) {
  const pairedProperties = useMemo(() => {
    // Most likely case is that we are joining from a Log -> Resource.
    // So expect Resource reference Property on left, and Resource uuid Property on right.
    let match = tryToGetPairedPropertiesForResourceTypeJoin({
      maybeReferencingDataset: stepInputDataset,
      maybeResourceTypeDataset: joinToDataset,
    });
    if (match) {
      return {
        leftPropertyToSet: match.resourceReferenceProperty,
        rightPropertyToSet: match.resourceIdProperty,
      };
    }

    // But sometimes, we might be joining from Resource -> Resource, and the
    // Resource reference property we want COULD be on the right.
    // If the Resource reference property is on the left, it will be caught by the case above.
    match = tryToGetPairedPropertiesForResourceTypeJoin({
      maybeReferencingDataset: joinToDataset,
      maybeResourceTypeDataset: stepInputDataset,
    });
    return match
      ? {
          rightPropertyToSet: match.resourceReferenceProperty,
          leftPropertyToSet: match.resourceIdProperty,
        }
      : undefined;
  }, [stepInputDataset, joinToDataset]);
  const { leftPropertyToSet, rightPropertyToSet } = pairedProperties || {};

  const alreadyHasConditionForResourceTypeJoin = useMemo(
    () =>
      !!conditions.value.find(
        (condition) =>
          leftPropertyToSet &&
          rightPropertyToSet &&
          condition.leftPropertyId === leftPropertyToSet.id &&
          condition.rightPropertyId === rightPropertyToSet.id,
      ),
    [conditions.value, leftPropertyToSet, rightPropertyToSet],
  );

  const setProperResourceIdConditionsClickHandler = useCallback(() => {
    if (
      !leftPropertyToSet ||
      !rightPropertyToSet ||
      alreadyHasConditionForResourceTypeJoin
    ) {
      return;
    }
    const firstViableConditionIndex = conditions.value.findIndex(
      (condition) =>
        (!condition.leftPropertyId ||
          condition.leftPropertyId === leftPropertyToSet.id) &&
        (!condition.rightPropertyId ||
          condition.rightPropertyId === rightPropertyToSet.id),
    );
    if (firstViableConditionIndex >= 0) {
      conditions.update(firstViableConditionIndex, {
        leftPropertyId: leftPropertyToSet.id,
        rightPropertyId: rightPropertyToSet.id,
      });
    } else {
      conditions.push({
        leftPropertyId: leftPropertyToSet.id,
        rightPropertyId: rightPropertyToSet.id,
      });
    }
  }, [
    leftPropertyToSet,
    rightPropertyToSet,
    alreadyHasConditionForResourceTypeJoin,
    conditions,
  ]);

  return leftPropertyToSet && rightPropertyToSet ? (
    <StyledInfoCard variant={InfoCardVariant.INFO}>
      Suggestion: since you are connecting to the{' '}
      <b>{getUserFriendlyResourceTypeDatasetName(joinToDataset)}</b>, you
      probably want to match <b>{leftPropertyToSet.name}</b> to{' '}
      <b>{rightPropertyToSet.name}</b>.
      <StyledButton
        color="primary"
        onClick={setProperResourceIdConditionsClickHandler}
        disabled={alreadyHasConditionForResourceTypeJoin}
      >
        Click here to enable this match.
      </StyledButton>
    </StyledInfoCard>
  ) : null;
}
